import { useState, useEffect } from 'react';
import { todayInputValue, formatNumber } from '../../../../utils/format';
import { useModalBackHandler } from '../../../../hooks/useModalBackHandler';
import { showToast } from '../../../../components/base/Toast';
import { adjustSupplyItem } from '../../../../store';
import { useAuth } from '../../../../hooks/useAuth';
import type { SupplyItem } from '../../../../types';

interface AdjustModalProps {
  item: SupplyItem | null;
  onClose: () => void;
}

export function AdjustModal({ item, onClose }: AdjustModalProps) {
  useModalBackHandler(true, onClose);
  const { user } = useAuth();
  const [form, setForm] = useState({ soLuongMoi: '', lyDo: '', ngay: todayInputValue() });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) setForm({ soLuongMoi: String(item.stock), lyDo: '', ngay: todayInputValue() });
  }, [item]);

  if (!item) return null;

  const handleSave = async () => {
    const soLuongMoi = Number(form.soLuongMoi);
    if (soLuongMoi < 0) { showToast('Số lượng không được âm', 'warning'); return; }
    if (!form.lyDo.trim()) { showToast('Vui lòng nhập lý do điều chỉnh', 'warning'); return; }
    if (!user?.htxId) return;

    const chenhLech = soLuongMoi - item.stock;
    setSaving(true);
    try {
      await adjustSupplyItem(item.id, soLuongMoi, form.lyDo, user.htxId);
      showToast(`Điều chỉnh kho thành công: ${chenhLech >= 0 ? '+' : ''}${chenhLech} ${item.unit}`, 'success');
      onClose();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Điều chỉnh kho thất bại', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40 animate-modal-backdrop" onClick={onClose} />
      <div onClick={(e) => e.stopPropagation()} className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto animate-modal-slide-up">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Điều chỉnh kho</h3>
            <p className="text-xs text-slate-400">{item.maVatTu} · {item.name}</p>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400"><i className="ri-close-line text-lg" /></button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className="bg-slate-50 rounded-xl p-3 flex items-center justify-between">
            <span className="text-sm text-slate-500">Tồn kho hiện tại</span>
            <span className="text-sm font-bold text-slate-900">{formatNumber(item.stock)} {item.unit}</span>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Số lượng mới <span className="text-red-500">*</span></label>
            <input type="number" value={form.soLuongMoi} onChange={(e) => setForm({ ...form, soLuongMoi: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder={`Số lượng mới (${item.unit})`} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Lý do điều chỉnh <span className="text-red-500">*</span></label>
            <textarea value={form.lyDo} onChange={(e) => setForm({ ...form, lyDo: e.target.value })} maxLength={500} rows={2} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder="VD: Kiểm kê định kỳ, hàng hỏng, điều chỉnh sai số..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Ngày điều chỉnh</label>
            <input type="date" value={form.ngay} onChange={(e) => setForm({ ...form, ngay: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" />
          </div>
          {form.soLuongMoi && (
            <div className={`rounded-xl p-3 text-sm ${Number(form.soLuongMoi) > item.stock ? 'bg-agrida-50' : Number(form.soLuongMoi) < item.stock ? 'bg-red-50' : 'bg-slate-50'}`}>
              <p className={Number(form.soLuongMoi) > item.stock ? 'text-agrida-700' : Number(form.soLuongMoi) < item.stock ? 'text-red-700' : 'text-slate-600'}>
                Chênh lệch: {Number(form.soLuongMoi) - item.stock >= 0 ? '+' : ''}{Number(form.soLuongMoi) - item.stock} {item.unit}
                {' · '}Tồn mới: {formatNumber(Number(form.soLuongMoi))} {item.unit}
              </p>
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]">Hủy</button>
          <button type="button" onClick={handleSave} disabled={saving} className="px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-agrida-700 hover:bg-agrida-800 disabled:opacity-60 transition-colors whitespace-nowrap min-h-[44px]">{saving ? 'Đang lưu...' : 'Lưu điều chỉnh'}</button>
        </div>
      </div>
    </div>
  );
}